import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listTrunks,
  createTrunk,
  updateTrunk,
  deleteTrunk,
  type TrunkListParams,
  type Trunk,
} from "../api/trunks";

const PAGE_SIZE = 50;

function TrunkEditor({
  trunk,
  onClose,
  onSaved,
}: {
  trunk: Trunk | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(trunk?.name ?? "");
  const [channelTech, setChannelTech] = useState(trunk?.channel_tech ?? "PJSIP");
  const [endpointName, setEndpointName] = useState(trunk?.endpoint_name ?? "");
  const [dialstring, setDialstring] = useState(trunk?.dialstring ?? "");
  const [serviceFlag, setServiceFlag] = useState(trunk?.service_flag ?? 1);
  const [channel, setChannel] = useState(trunk?.channel ?? 1);
  const [cps, setCps] = useState(trunk?.cps ?? 1);
  const [weight, setWeight] = useState(trunk?.weight ?? 1);
  const [isActive, setIsActive] = useState(trunk?.is_active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        channel_tech: channelTech,
        endpoint_name: endpointName || null,
        dialstring: dialstring || null,
        service_flag: serviceFlag,
        gateway_flag: trunk?.gateway_flag ?? 1,
        channel,
        cps,
        weight,
        is_active: isActive,
      };
      return trunk
        ? updateTrunk(trunk.trunk_id, body)
        : createTrunk(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{trunk ? "Edit" : "New"} trunk</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Channel technology</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={channelTech}
            onChange={(e) => setChannelTech(e.target.value)}
          >
            <option value="SIP">SIP</option>
            <option value="PJSIP">PJSIP</option>
            <option value="IAX2">IAX2</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Endpoint name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={endpointName}
            onChange={(e) => setEndpointName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Dialstring</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            value={dialstring}
            onChange={(e) => setDialstring(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Service flag</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={serviceFlag}
              onChange={(e) => setServiceFlag(Number(e.target.value))}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Capacity (channels)</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={channel}
              onChange={(e) => setChannel(Number(e.target.value))}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">CPS</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={cps}
              onChange={(e) => setCps(Number(e.target.value))}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Weight</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active
        </label>
        <p className="text-xs text-gray-400">
          Editing routing/capacity takes effect immediately. Provisioning a brand-new SIP carrier's
          transport (host/credentials) is configured separately in Asterisk.
        </p>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function TrunksPage() {
  const [params, setParams] = useState<TrunkListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    active_only: false,
  });

  const qc = useQueryClient();
  const [editor, setEditor] = useState<Trunk | "new" | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["trunks", params],
    queryFn: () => listTrunks(params),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteTrunk(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trunks"] }),
  });

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Trunks</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={params.active_only ?? false}
              onChange={(e) => setParams((p) => ({ ...p, active_only: e.target.checked, page: 1 }))}
            />
            Active only
          </label>
          <button
            onClick={() => setEditor("new")}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            New trunk
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} trunks`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Tech</th>
                <th className="px-4 py-2">Dialstring</th>
                <th className="px-4 py-2 text-right">Channels</th>
                <th className="px-4 py-2 text-right">CPS</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-gray-500">
                    No trunks.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((t) => (
                  <tr key={t.trunk_id}>
                    <td className="px-4 py-2 font-medium">{t.name}</td>
                    <td className="px-4 py-2">{t.channel_tech}</td>
                    <td className="px-4 py-2 font-mono text-gray-600">{t.dialstring || "—"}</td>
                    <td className="px-4 py-2 text-right">{t.channel}</td>
                    <td className="px-4 py-2 text-right">{t.cps}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          t.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {t.is_active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setEditor(t)} className="text-brand hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete trunk "${t.name}"?`)) remove.mutate(t.trunk_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editor && (
        <TrunkEditor
          trunk={editor === "new" ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["trunks"] });
          }}
        />
      )}
    </div>
  );
}
